import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Send, CheckCircle2, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SECTORS = [
  "Oceans / Maritime",
  "Agriculture / Agri-business",
  "Government / Defence",
  "Research / Academia",
  "Other",
];

export default function DemoSection() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", sector: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/demo-requests", form);
      setDone(true);
      toast.success("Request received. we'll be in touch within one business day.");
      setForm({ name: "", email: "", organization: "", sector: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-0 border-b border-white/15 bg-transparent px-0 py-3 text-white placeholder:text-slate-500 focus:border-arjuna-teal focus:outline-none focus:ring-0 transition-colors duration-300";

  return (
    <section id="demo" className="relative overflow-hidden bg-arjuna-ink py-24 sm:py-32" data-testid="demo-section">
      <div
        className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full opacity-20 blur-[120px]"
        style={{ background: "radial-gradient(circle, #0044FF, transparent 70%)" }}
      />
      <div className="container-x relative">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline">07. Get Started</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
                Request a demo.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                Tell us where you operate. coast, field, or command centre. and we&apos;ll
                tailor a walkthrough of the intelligence layers that matter to you.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 space-y-4 font-mono text-sm text-slate-400">
                <p><span className="text-arjuna-teal">→</span> Response within one business day</p>
                <p><span className="text-arjuna-teal">→</span> Live map & sample data access</p>
                <p><span className="text-arjuna-teal">→</span> No commitment required</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="glass rounded-2xl p-8 lg:p-10">
                {done ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center py-14 text-center"
                    data-testid="demo-success"
                  >
                    <CheckCircle2 className="h-14 w-14 text-arjuna-agri" />
                    <h3 className="mt-6 font-heading text-2xl text-white">Request received</h3>
                    <p className="mt-3 max-w-sm text-slate-400">
                      Thank you. A confirmation is on its way to your inbox and our team will
                      reach out shortly.
                    </p>
                    <Link
                      to="/sales"
                      data-testid="demo-sales-link"
                      className="btn-primary mt-8"
                    >
                      Talk to sales now
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <button
                      data-testid="demo-reset-btn"
                      onClick={() => setDone(false)}
                      className="mt-3 text-sm text-slate-400 transition-colors hover:text-arjuna-teal"
                    >
                      Submit another request
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="demo-form">
                    <div>
                      <label className="overline mb-1 block">Full name *</label>
                      <input data-testid="demo-name" className={inputCls} value={form.name} onChange={update("name")} placeholder="Aarav Sharma" />
                    </div>
                    <div>
                      <label className="overline mb-1 block">Work email *</label>
                      <input data-testid="demo-email" type="email" className={inputCls} value={form.email} onChange={update("email")} placeholder="you@org.in" />
                    </div>
                    <div>
                      <label className="overline mb-1 block">Organization</label>
                      <input data-testid="demo-org" className={inputCls} value={form.organization} onChange={update("organization")} placeholder="Company / Agency" />
                    </div>
                    <div>
                      <label className="overline mb-1 block">Sector</label>
                      <Select value={form.sector} onValueChange={(v) => setForm((f) => ({ ...f, sector: v }))}>
                        <SelectTrigger
                          data-testid="demo-sector"
                          className="mt-1 w-full rounded-none border-0 border-b border-white/15 bg-transparent px-0 text-white focus:ring-0"
                        >
                          <SelectValue placeholder="Select a sector" />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-arjuna-surface text-white">
                          {SECTORS.map((s) => (
                            <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-arjuna-teal">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="overline mb-1 block">What do you want to monitor?</label>
                      <textarea data-testid="demo-message" rows={3} className={inputCls} value={form.message} onChange={update("message")} placeholder="e.g. Illegal fishing along the Kerala coast; drought stress in Vidarbha…" />
                    </div>
                    <div className="sm:col-span-2">
                      <button type="submit" disabled={loading} data-testid="demo-submit" className="btn-primary w-full sm:w-auto">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {loading ? "Sending…" : "Request Demo"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
